/**
 * Seed orders by email (D69): the garden's private address, and the seeds waiting for a tap in its Seed vault. The
 * server decides everything (whether the plan allows it, the address, what is filed); the app shows it.
 *
 * Endpoints (every answer also carries `success`):
 * - GET    /seed-orders/mine → SeedOrdersMine (the address is made on the first read when the plan allows it).
 * - POST   /seed-orders/address/new → { address } ("New address"; the garden's owner only: 403 OWNER_ONLY).
 * - GET    /seed-orders/held → { held: HeldSeed[] }.
 * - POST   /seed-orders/held/:id/file { plantId, varietyId? | newVariety?, kind? } or { cropId, typeUnsure: true }
 *          → { plantObjId, joined } (402 FREE_PLANT_LIMIT with the shared room words; the row stays).
 * - DELETE /seed-orders/held/:id ("Not a seed").
 *
 * Who uses it: features/seed-orders (SeedOrdersScreen, SeedAddressCard, HeldSeeds). Caching: the address under
 * SEED_ORDERS_MINE_KEY, the held seeds under SEED_ORDERS_HELD_KEY (features/seed-orders/lib/seedOrderQueries).
 * Auth: the signed-in token, in the garden being tended.
 *
 * @module api/seedOrders
 */

import { apiClient } from './apiClient';
import type { PacketItem, ScannedPacket } from './packets';

/** The garden's address, as the profile screen and the vault's empty state show it. */
export interface SeedOrdersMine {
  /** The plan allows reading seed orders (the trial and Grow More). */
  allowed: boolean;
  /** The whole address ("amy-k7q4x2@seeds.gardengameplan.com"); null on Free with none made. */
  address: string | null;
  /** The part after the @. */
  domain: string;
  /** The garden's Seed vault, or null when there is none yet. */
  vaultLocationId: number | null;
  /** Seeds waiting for a tap. */
  held: number;
  /** The garden's owner (only the owner may ask for a new address). */
  isOwner: boolean;
}

/**
 * A kept seed, shaped as the packet review's packet, with its id, amount and note: an emailed seed Grow could not place
 * (unplaced), or (B137) a packet kept from a photo, which carries the plant, variety and name the gardener settled and
 * its cut-out.
 */
export interface HeldSeed extends ScannedPacket {
  /** The held row's id. */
  id: number;
  /** As the order printed it ("1/4 gram"), or null. */
  amount: string | null;
  /** The note it gets when filed ("Territorial Seed Company · 1/4 gram · ordered Oct 20, 2026"). */
  note: string;
  /** B137: a packet kept from a photo has its cut-out, signed; null for an emailed seed or one without. */
  photo_url?: string | null;
}

/**
 * The garden's address and its vault.
 *
 * @returns SeedOrdersMine.
 */
export async function getSeedOrdersMine(): Promise<SeedOrdersMine> {
  const response = await apiClient.get<SeedOrdersMine & { success: boolean }>('/seed-orders/mine');
  return response.data;
}

/**
 * "New address": the part after the hyphen drawn again; the old address stops working at once.
 *
 * @returns The new address.
 */
export async function newSeedAddress(): Promise<string> {
  const response = await apiClient.post<{ success: boolean; address: string }>('/seed-orders/address/new', {});
  return response.data.address;
}

/**
 * The seeds waiting for a tap, newest first.
 *
 * @returns The held seeds.
 */
export async function getHeldSeeds(): Promise<HeldSeed[]> {
  const response = await apiClient.get<{ success: boolean; held: HeldSeed[] }>('/seed-orders/held');
  return response.data.held;
}

/**
 * Files one kept seed into the vault at Planning ("Plan to grow it").
 *
 * @param id - The held row.
 * @param item - As one POST /packets/confirm item (no box, no bed: it goes to the vault).
 * @returns The plant made or joined.
 */
export async function fileHeldSeed(id: number, item: Omit<PacketItem, 'box' | 'locationId' | 'newBedName'>): Promise<{ plantObjId: number; joined: boolean }> {
  const response = await apiClient.post<{ success: boolean; plantObjId: number; joined: boolean }>(`/seed-orders/held/${id}/file`, item);
  return { plantObjId: response.data.plantObjId, joined: response.data.joined };
}

/**
 * "Not a seed": the held row goes and nothing is filed.
 *
 * @param id - The held row.
 */
export async function removeHeldSeed(id: number): Promise<void> {
  await apiClient.delete(`/seed-orders/held/${id}`);
}
